import type {
  InvoiceImportCandidate,
  InvoiceImportSource,
  ParsedInvoiceDraft,
  ParsedInvoiceLine,
  PaymentScheduleEntry,
} from "../../features/invoice-import/types";
import type { ApiImportBundle, ApiImportListItem } from "../invoiceImportTypes";

function num(value: string | number | null | undefined): number | undefined {
  if (value == null || value === "") return undefined;
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function mapLine(line: ApiImportBundle["lines"][number]): ParsedInvoiceLine {
  return {
    id: line.id,
    rawLine: line.rawLine,
    name: line.name,
    description: line.description ?? undefined,
    rawQuantity: num(line.rawQuantity) ?? 0,
    rawUnitLabel: line.rawUnitLabel ?? undefined,
    interpretedUnit: line.interpretedUnit ?? undefined,
    interpretedQuantity: num(line.interpretedQuantity),
    unitPrice: num(line.unitPrice) ?? 0,
    lineTotal: num(line.lineTotal) ?? 0,
    classification: line.classification,
    needsReview: line.needsReview,
    rawNotes: line.rawNotes ?? undefined,
  };
}

export function mapImportBundleToCandidate(bundle: ApiImportBundle): InvoiceImportCandidate {
  const importRow = bundle.import;
  const draftRow = bundle.draft;
  const source: InvoiceImportSource = {
    kind: importRow.sourceKind,
    filename: importRow.sourceFilename,
    url: importRow.sourceUrl ?? undefined,
  };

  let draft: ParsedInvoiceDraft | undefined;
  if (draftRow) {
    const paymentSchedule = (bundle.paymentSchedule ?? []).map(
      (entry): PaymentScheduleEntry => ({
        amount: num(entry.amount),
        dueDate: entry.dueDate ?? undefined,
        paymentDate: entry.paymentDate ?? undefined,
        paymentNumber: entry.paymentNumber ?? undefined,
        status: entry.status ?? undefined,
        rawLine: entry.rawLine,
      }),
    );

    draft = {
      sourceFilename: importRow.sourceFilename,
      clientName: draftRow.clientName ?? undefined,
      clientEmail: draftRow.clientEmail ?? undefined,
      clientPhone: draftRow.clientPhone ?? undefined,
      invoiceNumber: draftRow.invoiceNumber ?? undefined,
      invoiceIssueDate: draftRow.invoiceIssueDate ?? undefined,
      projectName: draftRow.projectName ?? undefined,
      eventType: draftRow.eventType ?? undefined,
      eventDate: draftRow.eventDate ?? undefined,
      venue: draftRow.venue ?? undefined,
      subtotal: num(draftRow.subtotal),
      invoiceTotal: num(draftRow.invoiceTotal),
      parseWarnings: Array.isArray(draftRow.parseWarnings)
        ? (draftRow.parseWarnings as string[])
        : [],
      lines: bundle.lines.map(mapLine),
      paymentSchedule,
    };
  }

  const parseStatus = importRow.parseStatus === "imported"
    ? "parsed"
    : importRow.parseStatus === "ready"
      ? "parsed"
      : importRow.parseStatus;

  return {
    id: importRow.id,
    source,
    parseStatus,
    draft,
    error: importRow.parseError ?? undefined,
    markedReady: importRow.markedReady || importRow.parseStatus === "ready" || importRow.parseStatus === "imported",
    importedEventId: importRow.importedEventId ?? undefined,
    importedAt: importRow.importedAt ?? undefined,
    hasSourceDocument: Boolean(bundle.sourceDocument),
  };
}

export function mapListItemToCandidate(item: ApiImportListItem): InvoiceImportCandidate {
  const source: InvoiceImportSource = {
    kind: item.sourceKind,
    filename: item.sourceFilename,
    url: item.sourceUrl ?? undefined,
  };

  const draftSummary = item.draftSummary;
  const draft: ParsedInvoiceDraft | undefined = draftSummary
    ? {
        sourceFilename: item.sourceFilename,
        clientName: draftSummary.clientName ?? undefined,
        invoiceNumber: draftSummary.invoiceNumber ?? undefined,
        projectName: draftSummary.projectName ?? undefined,
        eventType: draftSummary.eventType ?? undefined,
        eventDate: draftSummary.eventDate ?? undefined,
        lines: [],
        paymentSchedule: [],
        parseWarnings: [],
      }
    : undefined;

  const parseStatus = item.parseStatus === "imported" || item.parseStatus === "ready" ? "parsed" : item.parseStatus;

  return {
    id: item.id,
    source,
    parseStatus,
    draft,
    markedReady: item.markedReady || item.parseStatus === "ready" || item.parseStatus === "imported",
    importedEventId: item.importedEventId ?? undefined,
    importedAt: item.importedAt ?? undefined,
  };
}

export function draftToCreatePayload(params: {
  source: InvoiceImportSource;
  draft: ParsedInvoiceDraft;
  parseError?: string;
}) {
  return {
    sourceKind: params.source.kind,
    sourceFilename: params.source.filename,
    sourceUrl: params.source.url,
    parseStatus: "parsed" as const,
    parseError: params.parseError,
    invoiceNumber: params.draft.invoiceNumber,
    draft: {
      clientName: params.draft.clientName,
      clientEmail: params.draft.clientEmail,
      clientPhone: params.draft.clientPhone,
      invoiceNumber: params.draft.invoiceNumber,
      invoiceIssueDate: params.draft.invoiceIssueDate,
      projectName: params.draft.projectName,
      eventType: params.draft.eventType,
      eventDate: params.draft.eventDate,
      venue: params.draft.venue,
      subtotal: params.draft.subtotal,
      invoiceTotal: params.draft.invoiceTotal,
      parseWarnings: params.draft.parseWarnings,
      parserPayload: params.draft,
    },
    lines: params.draft.lines.map((line) => ({
      rawLine: line.rawLine,
      name: line.name,
      description: line.description,
      rawQuantity: line.rawQuantity,
      rawUnitLabel: line.rawUnitLabel,
      interpretedUnit: line.interpretedUnit,
      interpretedQuantity: line.interpretedQuantity,
      unitPrice: line.unitPrice,
      lineTotal: line.lineTotal,
      classification: line.classification,
      needsReview: line.needsReview,
      rawNotes: line.rawNotes,
      parserPayload: line,
    })),
    paymentSchedule: params.draft.paymentSchedule,
  };
}

export function draftToPatchPayload(draft: ParsedInvoiceDraft) {
  return {
    clientName: draft.clientName,
    clientEmail: draft.clientEmail,
    clientPhone: draft.clientPhone,
    invoiceNumber: draft.invoiceNumber,
    invoiceIssueDate: draft.invoiceIssueDate,
    projectName: draft.projectName,
    eventType: draft.eventType,
    eventDate: draft.eventDate,
    venue: draft.venue,
    subtotal: draft.subtotal,
    invoiceTotal: draft.invoiceTotal,
    parseWarnings: draft.parseWarnings,
    reviewedPayload: draft,
  };
}
